/** 学习域接口的类型与调用:与 apps/api 的 /v1/learning、/v1/parent 一一对应,页面只认这些类型 */
import type { ApiClient } from './client.js';
import type { CourseProfile, GradeSheet, RubricBrief, TaskList } from './courseForms.js';

export interface CompletionState {
  lecture: boolean;
  interaction: boolean;
  practice: boolean;
  note: boolean;
  homework: boolean;
}
export interface PassRecord {
  score: number;
  grade: string;
  correctRate: number;
  completion: number;
  efficiency: number;
  durationSec: number;
  at: string;
  classOnly?: boolean;
  trial?: boolean;
  /** teacher = 教师介入后「判定通过」(不进榜) */
  source?: 'teacher';
  reason?: string;
}
export interface RemedyRecord {
  round: number;
  weakPoints: string[];
  retestQuestionIds: string[];
  retestRate: number | null;
  result: 'pending' | 'passed' | 'failed';
  at: string;
  /** 复测已交、有题在等批改(老轮次) */
  submittedAt?: string;
}
/** 课程权限到期 / 被收回:已开始的关只读 */
export interface ReadonlyInfo {
  reason: 'not_entitled' | 'trial_limit' | 'outside_zone';
  text: string;
}
export type LevelStatus =
  | 'locked'
  | 'unlocked'
  | 'learning'
  | 'pending_grading'
  | 'passed'
  | 'failed'
  | 'remedying'
  | 'teacher_intervene';

export interface JourneyPart {
  id: string;
  seq: number;
  name: string;
  knowledge: string;
  goal: string;
  status: 'done' | 'current' | 'available' | 'locked' | 'paid' | 'referenced_done';
  passed: number;
  total: number;
  courseId: string | null;
  levelFrom: number | null;
  levelTo: number | null;
  referenced: boolean;
  /** 为什么还没解锁(上一段没完成 / 上一段结业测评没达标) */
  lockNote?: string | null;
  assessment: {
    kind: string;
    available: boolean;
    /** in_progress = 进行中可以继续作答 · submitted = 已交卷待出分 · graded = 已出分 */
    state?: AssessmentState;
    done: boolean;
    passed: boolean | null;
    /** 已出分的成绩(A8-1:限一次,做完入口置灰并写成绩) */
    score?: number | null;
    maxScore?: number | null;
    remainingSec?: number | null;
    /** 低龄图标积木课的测评:入口链接带上 ?jr=1,测评页直接出平板版式 */
    iconBlocks?: boolean;
  } | null;
}
export interface JourneyLine {
  productLine: {
    id: string;
    code: string;
    name: string;
    tagline: string | null;
    intro: string | null;
    kind: string;
    freeParts: number;
    /** 要开通的采购模块(AI 课程产品线 = ai_course) */
    requiredModule?: string | null;
  };
  entitled: boolean;
  parts: JourneyPart[];
  next: { partId: string; courseId: string; levelId: string | null; levelSeq: number | null } | null;
  /** AI 课程产品线:实操消耗 AI 算力额度(原型「额度说明」) */
  aiQuota?: {
    freeTokens: number;
    freeUsed: number;
    student: number;
    orgPool: boolean;
    state: 'normal' | 'exceeded';
  } | null;
}
export interface Journey {
  lines: JourneyLine[];
  hidden: { id: string; name: string }[];
}

export interface MapLevel {
  id: string;
  seq: number;
  name: string;
  goal: string;
  lessonHours: number;
  standardMinutes: number;
  access: string;
  prerequisiteSeqs: number[];
  status: LevelStatus;
  /** outside_zone = 校外课程(学校免费专区之外);final_assessment = 上一部分结业测评达标后解锁 */
  lockReason: 'prerequisite' | 'trial_limit' | 'not_entitled' | 'outside_zone' | 'final_assessment' | null;
  /** 被结业测评拦住时:要先达标的那一部分 */
  gatePart?: { id: string; name: string } | null;
  /** 已开始的关,课程权限到期 / 被收回:只读 */
  readonly?: boolean;
  completion: CompletionState | null;
  firstPass: PassRecord | null;
  latest: PassRecord | null;
  passCount: number;
}
export interface CourseMap {
  course: {
    id: string;
    name: string;
    track: string;
    chapters: { seq: number; name: string; levelFrom: number; levelTo: number }[];
    /** 二期三类课程:教材版本(学科)与体系属性(年级学期 / 职业方向阶段 / AI 等级) */
    textbookVersion?: string | null;
    profile?: CourseProfile | null;
  };
  access: 'full' | 'trial' | 'none';
  levels: MapLevel[];
  next: MapLevel | null;
  /** 学校免费专区:开放到第几关、之后的关隐藏还是标「校外课程」 */
  zone?: { limit: number; outside: 'hidden' | 'labeled' } | null;
  /**
   * 开通这门课的机构现在只读(到期宽限 / 冻结 / 待删除):地图、成绩与记录照常看,进关与作答会被拦;
   * text 是给学员看的说法(哪家机构开的课 + 只读期的说法)
   */
  ownerReadonly?: { name: string; text: string } | null;
  /** 低龄图标积木课(二期 ScratchJr):走平板版的地图与关卡页(/jr/…) */
  iconBlocks?: boolean;
}

export interface StudentQuestion {
  id: string;
  number: string;
  type: string;
  stem: string;
  stemImage: string | null;
  audio: string | null;
  options: { key: string; text: string }[] | null;
  maxScore: number;
  standardMinutes: number;
  knowledgePointIds: string[];
  attachmentTypes: string[] | null;
  sampleCases: { input: string; expected: string }[] | null;
  limits: { timeLimitMs: number | null; memoryLimitMb: number | null } | null;
  blanks: number | null;
  manualReview: boolean;
  /** 多选的判分方式:partial 少选得一半 / all_or_nothing 全对才得分(A20-1) */
  multiScoring?: string | null;
  /** 图标积木任务(二期 ScratchJr · 题型 jr_blocks):舞台、角色、道具、给哪些积木、目标(@cg/shared JrPuzzle) */
  jr?: unknown;
}
export interface SubmissionSummary {
  id: string;
  status: string;
  attemptNo: number;
  correct: boolean | null;
  score: number | null;
  maxScore: number | null;
  feedback: string | null;
  /** 按评分表评的作品:各项得分(出分后才有;二期) */
  sheet?: GradeSheet | null;
  /** 图标积木任务:这一次拿了几颗星(别的题型为 null) */
  stars?: number | null;
  submittedAt: string;
}
export interface LevelSession {
  /** 这次进关把这一关换成了新版本内容(还没交过作答的才换) */
  contentUpgraded?: boolean;
  /** 有新版本,但交过作答没自动换:可以「换成新版从头学」 */
  newerContent?: boolean;
  level: {
    id: string;
    seq: number;
    name: string;
    goal: string;
    knowledgePointIds: string[];
    lessonHours: number;
    standardMinutes: number;
    threshold: number;
    homeworkSubmitModes: string[];
    noteTemplate: string | null;
    lecture: {
      type: string;
      /** 外链,或上传视频转码后的限时播放地址 */
      url: string | null;
      /** 上传视频的封面(限时地址) */
      posterUrl: string | null;
      /** 上传的视频还没转好:进度 / 失败原因(这时 url 为空) */
      transcoding: { status: string; progress: number; error: string | null } | null;
      /** 字幕(WebVTT) */
      subtitleUrl: string | null;
      content: string | null;
      durationSec: number;
      interactionCount: number;
      /** 数字人交互点:视频播到第几秒数字人提问 / 演示 / 总结 */
      dhCues: { atSec: number; kind: 'ask' | 'demo' | 'summary'; text: string }[];
      /** 这关有 AI 实操(AI 课程产品线):详情与额度走 /v1/ai-lab */
      aiLab?: { purpose: string; task: string } | null;
      /** 图形化工作室关卡(讲解 + 跟做合成几个回合):页面转到 /studio/levels/:id */
      studio?: boolean;
    };
    interactions: { index: number; atSec: number; question: string; options: string[] }[];
    /** 随堂实操任务清单(二期 AI / 职教);requireRun = 课后作品要附沙箱运行结果 */
    practical?: (TaskList & { requireRun: boolean }) | null;
  };
  course: {
    id: string;
    name: string;
    track: string;
    chapters: { seq: number; name: string; levelFrom: number; levelTo: number }[];
    /** 课程体系与这个体系的叫法 / 判定方式(二期三类课程) */
    family?: string;
    form?: {
      stages: string[];
      practiceLabel: string;
      homeworkLabel: string;
      passRule: 'average' | 'both';
      /** 低龄图标积木课(二期 ScratchJr):整关都是图标积木任务时走平板端关卡页 */
      iconBlocks?: boolean;
    };
    /** 「七年级上 · 人教版 2024」「前端开发 · 进阶」;没有为空串 */
    profile?: string;
  };
  progress: {
    status: LevelStatus;
    completion: CompletionState;
    lecturePositionSec: number;
    lectureFirstDone: boolean;
    interactionState: { answered: number[]; correct: number[] };
    practiceRate: number | null;
    homeworkRate: number | null;
    homeworkAttempt: number;
    firstPass: PassRecord | null;
    latest: PassRecord | null;
    passCount: number;
    remedy: RemedyRecord[];
    remedyCount: number;
    /** 老师「放回学习」时已补强的轮数(第几次补强 = 轮次 − 它) */
    remedyBase?: number;
    /** 第几次补强没过转老师(平台规则) */
    remedyLimit?: number;
    /** 已通过的关正在重做这一轮(只更新个人记录) */
    redoOpen?: boolean;
    /** 现在能交作业 */
    canSubmitHomework?: boolean;
    /** 课程到期 / 被收回:只读 */
    readonly?: ReadonlyInfo | null;
  };
  practice: { question: StudentQuestion; submission: SubmissionSummary | null }[];
  homework: {
    question: StudentQuestion;
    submission: SubmissionSummary | null;
    /** 作品题挂的评分表(二期):交之前就能看到按什么评 */
    rubric?: RubricBrief | null;
  }[];
  retest: StudentQuestion[];
  note: { content: string; aiGenerated: boolean } | null;
}
export interface AnswerInput {
  questionId: string;
  answer: unknown;
  durationSec: number;
}
/** 判定方式(二期):average 两项平均 ≥ 阈值;both 两项各自 ≥ 阈值(职教项目型) */
export type PassRule = 'average' | 'both';
export interface Settlement {
  passed: boolean;
  record: PassRecord;
  practiceRate: number;
  homeworkRate: number;
  threshold: number;
  first: boolean;
  unlockedLevelIds: string[];
  hours: { balance: number; negative: boolean } | null;
  badges: string[];
  /** 已通过的关重做的这一轮(只更新个人记录) */
  redo?: boolean;
}
export interface PracticeResult {
  rate: number;
  items: {
    questionId: string;
    status: string;
    correct: boolean | null;
    score: number | null;
    maxScore: number | null;
    detail: boolean[] | null;
    /** 答错时的提示(不含答案,A4-2) */
    hint?: string | null;
  }[];
  completion: CompletionState;
}
export interface HomeworkResult {
  items: {
    questionId: string;
    status: string;
    correct: boolean | null;
    score: number | null;
    maxScore: number | null;
  }[];
  completion: CompletionState;
  settlement: Settlement | null;
}
/**
 * 结算页 · 逐题用时:每题最新一次提交记下的作答时长(和效率分是同一份数)。
 * verdict:guess 太快(按猜答,不给效率分)· ok 正常 · slow 偏慢(按比例扣)· over 太慢(效率 0);band = 效率区间(倍标准用时)
 */
export interface LevelTimingRow {
  part: 'practice' | 'homework' | 'retest' | 'assessment';
  no: number;
  questionId: string;
  type: string;
  title: string;
  durationSec: number;
  standardSec: number;
  correct: boolean | null;
  counted: boolean;
  verdict: 'guess' | 'ok' | 'slow' | 'over';
}
export interface LevelTiming {
  band: readonly [number, number];
  rows: LevelTimingRow[];
}
export interface LevelResult {
  status: LevelStatus;
  threshold: number;
  /** 二期:判定方式(职教项目型 = both,清单与评审各自到线) */
  passRule?: PassRule;
  practiceRate: number | null;
  homeworkRate: number | null;
  firstPass: PassRecord | null;
  latest: PassRecord | null;
  passCount: number;
  completion: CompletionState;
  missing: string[];
  remedy: RemedyRecord[];
  remedyCount: number;
  remedyBase?: number;
  remedyLimit?: number;
  redoOpen?: boolean;
  readonly?: ReadonlyInfo | null;
  courseId?: string;
  lessonHours: number;
  hoursDeducted: boolean;
  /** 转教师后要先复习的前置关卡(本人状态) */
  review?: { levelId: string; seq: number; name: string; status: string }[];
}
export interface RemedyPlan {
  /** 放回学习后重新数的第几次补强 */
  round: number;
  /** 第几次没过转老师 */
  limit?: number;
  /** 这一轮复测已交、有题在等老师批改 */
  waiting?: boolean;
  weakPoints: { id: string; name: string }[];
  lecture: {
    type: string;
    url: string | null;
    posterUrl: string | null;
    subtitleUrl: string | null;
    content: string | null;
  };
  retest: StudentQuestion[];
}
export interface RetestResult {
  passed: boolean;
  /** 有题在等批改时为空 */
  rate: number | null;
  round: number;
  settlement: Settlement | null;
  teacherIntervene: boolean;
  waiting?: boolean;
}
export interface Board {
  entries: {
    rank: number;
    studentId: string;
    nickname: string;
    score: number;
    grade: string;
    durationSec: number;
    me: boolean;
  }[];
  /** rank:本人名次(小低 / 没成绩为 null;在前 N 名外也给);inTop:在不在上面的名单里 */
  me: {
    rank: number | null;
    surpass: number | null;
    grade: string | null;
    hideRank: boolean;
    inTop?: boolean;
  };
  total: number;
  /** 实际用的范围(不传按机构规则的默认范围)、能切到的范围、前 N 名 */
  scope?: BoardScope;
  scopes?: { class: boolean; platform: boolean };
  topN?: number;
  /** 学校关了校内榜单 */
  disabled?: boolean;
}
export type BoardScope = 'org' | 'class' | 'platform';
export const BOARD_SCOPE_LABELS: Record<BoardScope, string> = {
  class: '班级内',
  org: '机构内',
  platform: '全平台',
};
export interface Overview {
  student: {
    id: string;
    nickname: string;
    grade: string | null;
    schoolStage: string;
    group: string | null;
    lessonHours: number;
  };
  streakDays: number;
  activeDays: string[];
  passedLevels: number;
  averageScore: number | null;
  badges: { key: string; earnedAt: string; ref: Record<string, string> | null }[];
  recentPasses: {
    levelId: string;
    levelName: string;
    seq: number | null;
    score: number;
    grade: string;
    passedAt: string | null;
  }[];
  pendingGrading: number;
  inRemedy: number;
  wrongOpen: number;
}
/** 测评状态:没配 / 没开放 / 可以开始 / 进行中(可续答)/ 已交卷待出分 / 已出分 */
export type AssessmentState = 'none' | 'locked' | 'open' | 'in_progress' | 'submitted' | 'graded';
export interface AssessmentAvailability {
  partId: string;
  /** unit = 学科课的单元测(二期:记成绩、不进榜、不阻断) */
  kind: 'mid' | 'final' | 'unit' | 'none';
  available: boolean;
  state?: AssessmentState;
  reason: string | null;
  attempt: AssessmentAttempt | null;
  passScore: number | null;
  /** 进行中:还剩多少秒(到点服务端按最后保存的作答代交) */
  remainingSec?: number | null;
  /** 测评所在的课程(做完回这门课的地图) */
  courseId?: string | null;
  /** 低龄图标积木课(二期 ScratchJr)的测评:测评页走平板 / 触屏版式 */
  iconBlocks?: boolean;
}
export interface AssessmentAttempt {
  id: string;
  partId: string;
  kind: string;
  status: 'in_progress' | 'submitted' | 'graded';
  startedAt: string;
  deadlineAt: string;
  submittedAt: string | null;
  /** manual = 本人交卷 · auto = 到点系统代交 */
  submitMode?: 'manual' | 'auto' | null;
  score: string | null;
  maxScore: number;
  passed: number | null;
  report: {
    perKnowledge: { id: string; correct: number; total: number }[];
    wrongQuestionIds: string[];
  } | null;
}
/** 开始 / 续答拿到的:题目、已保存的作答、剩余时间(截止时间不顺延) */
export interface AssessmentSession {
  attemptId: string;
  deadlineAt: string;
  durationMin: number;
  remainingSec: number;
  questions: StudentQuestion[];
  draft: Record<string, { answer: unknown; durationSec: number }>;
}
export interface AssessmentReport {
  attempt: AssessmentAttempt;
  /** 还有题在等老师批改(页面显示「待批改」,不显示 0 分) */
  pending?: boolean;
  pendingCount?: number;
  rank: number | null;
  total: number;
  knowledge: { id: string; name: string; correct: number; total: number; mastery: number }[];
}
export interface DrillPack {
  id: string;
  productLineId: string;
  name: string;
  description: string | null;
  questionCount: number;
  access: string;
  priceCents: number | null;
  unlocked: boolean;
}
export interface DrillSession {
  id: string;
  packId: string;
  mode: string;
  questionIds: string[];
  answered: number;
  correct: number;
  durationSec: number;
  timeLimitSec: number | null;
  startedAt: string;
  finishedAt: string | null;
}
export interface DrillAnswer {
  /** 还在排队评测的(编程题)为 null:显示「评测中」,出结果后错的再进错题本 */
  correct: boolean | null;
  pending?: boolean;
  score: number | null;
  maxScore: number;
  detail: boolean[] | null;
  explanation: string | null;
  answered: number;
  total: number;
}
export interface WrongBook {
  items: {
    id: string;
    questionId: string;
    source: string;
    sourceLabel: string | null;
    knowledgePointIds: string[];
    wrongCount: number;
    corrected: number;
    /** 同类题已经连对几道(到 2 道算订正;服务端原样返回的错题行字段) */
    correctStreak?: number;
    /** 订正的时间;没订正 = null */
    correctedAt?: string | null;
    lastWrongAt: string;
    question: StudentQuestion | null;
  }[];
  knowledge: {
    id: string;
    name: string;
    mastery: number | null;
    /** 对应关卡;没解锁时 condition 写解锁条件(A17-4) */
    level: {
      levelId: string;
      seq: number;
      name: string;
      unlocked: boolean;
      condition?: string | null;
    } | null;
  }[];
  sessions: DrillSession[];
}
export interface ParentSummary {
  student: {
    id: string;
    name: string;
    nickname: string;
    grade: string | null;
    lessonHours: number;
    hideRank: boolean;
  };
  week: {
    activeDays: number;
    /** 小学低年级:score 为空,只给等级与超越百分比(A2-2) */
    passed: {
      levelName: string;
      score: number | null;
      grade: string | null;
      surpass?: number | null;
      at: string | null;
    }[];
    badges: string[];
  };
  streakDays: number;
  totalPassed: number;
  notices: string[];
}
export interface Entitlement {
  id: string;
  courseId: string;
  productLineId: string | null;
  source: string;
  startsAt: string;
  expiresAt: string | null;
  trialLevelLimit: number | null;
  status: string;
}

export const BADGE_LABELS: Record<string, string> = {
  first_pass: '首次通关',
  streak_3: '连续通关 3 关',
  grade_s: '获得 S 评级',
  no_remedy_5: '5 关一次过',
  assessment_mid: '完成中期测评',
  assessment_final: '完成结业测评',
};
export const LEVEL_STATUS_LABELS: Record<LevelStatus, string> = {
  locked: '未解锁',
  unlocked: '已解锁',
  learning: '学习中',
  pending_grading: '待批改',
  passed: '已通过',
  failed: '未达标',
  remedying: '补强中',
  teacher_intervene: '教师介入',
};

export function learningApi(api: ApiClient) {
  const b = '/v1/learning';
  return {
    journey: () => api.get<Journey>(`${b}/journey`),
    overview: () => api.get<Overview>(`${b}/overview`),
    entitlements: () => api.get<Entitlement[]>(`${b}/entitlements`),
    /** 请家长开通(家长端收站内信;同一项一天一次,再点 again = true) */
    unlockRequest: (target: 'course' | 'drill_pack', id: string) =>
      api.post<{ again: boolean }>(`${b}/unlock-requests`, { target, id }),
    map: (courseId: string) => api.get<CourseMap>(`${b}/courses/${courseId}/map`),
    enter: (levelId: string) => api.post<LevelSession>(`${b}/levels/${levelId}/enter`),
    /** 换成新版从头学(有新版本、交过作答没自动换的) */
    upgrade: (levelId: string) => api.post<LevelSession>(`${b}/levels/${levelId}/upgrade`, {}),
    /** keepalive:关页面 / 切走时存进度用(浏览器在页面卸载后也会把这次请求发完) */
    lecture: (
      levelId: string,
      body: { positionSec: number; done?: boolean; activeSec?: number },
      opts: { keepalive?: boolean } = {},
    ) =>
      api.put<CompletionState>(
        `${b}/levels/${levelId}/lecture`,
        body,
        opts.keepalive ? { keepalive: true } : undefined,
      ),
    interaction: (levelId: string, index: number, answer: number) =>
      api.post<{ correct: boolean; hint: string | null; completion: CompletionState }>(
        `${b}/levels/${levelId}/interactions/${index}`,
        { answer },
      ),
    /** aiGenerated = 这份笔记是从「AI 生成笔记」的草稿改出来的 */
    note: (levelId: string, content: string, aiGenerated = false) =>
      api.put<CompletionState>(`${b}/levels/${levelId}/note`, { content, aiGenerated }),
    practice: (levelId: string, answers: AnswerInput[]) =>
      api.post<PracticeResult>(`${b}/levels/${levelId}/practice`, { answers }),
    homework: (levelId: string, answers: AnswerInput[]) =>
      api.post<HomeworkResult>(`${b}/levels/${levelId}/homework`, { answers }),
    result: (levelId: string) => api.get<LevelResult>(`${b}/levels/${levelId}/result`),
    /** 结算页 · 逐题用时(展开时才取) */
    timing: (levelId: string) => api.get<LevelTiming>(`${b}/levels/${levelId}/result/timing`),
    remedy: (levelId: string) => api.post<RemedyPlan>(`${b}/levels/${levelId}/remedy`),
    retest: (levelId: string, answers: AnswerInput[]) =>
      api.post<RetestResult>(`${b}/levels/${levelId}/retest`, { answers }),
    redo: (levelId: string) => api.post<{ attempt: number; redo: boolean }>(`${b}/levels/${levelId}/redo`),
    /** 关卡榜:默认机构内总榜;scope=platform 全平台,period=week|month 周榜 / 月榜 */
    board: (levelId: string, opts: { scope?: BoardScope; period?: 'week' | 'month' | 'all' } = {}) =>
      api.get<Board>(`${b}/levels/${levelId}/board`, { query: { scope: opts.scope, period: opts.period } }),
    // 数字人答疑在 api/dh.ts(/v1/dh):席位 + 每月额度 + 测评考试中不可用
    assessment: (partId: string) => api.get<AssessmentAvailability>(`${b}/parts/${partId}/assessment`),
    /** 开始测评;已经开始了的(刷新 / 断线回来)直接续答 */
    assessmentStart: (partId: string) => api.post<AssessmentSession>(`${b}/parts/${partId}/assessment/start`),
    assessmentResume: (attemptId: string) =>
      api.get<AssessmentSession>(`${b}/assessments/${attemptId}/resume`),
    /** 保存答到一半的作答(截止 + 1 分钟后拒 E0105) */
    assessmentDraft: (attemptId: string, answers: AnswerInput[]) =>
      api.put<{ savedAt: string }>(`${b}/assessments/${attemptId}/draft`, { answers }),
    /** 交卷;已过截止的按最后保存的作答代交,late = true */
    assessmentSubmit: (attemptId: string, answers: AnswerInput[]) =>
      api.post<AssessmentAttempt & { late?: boolean }>(`${b}/assessments/${attemptId}/submit`, { answers }),
    assessmentReport: (partId: string) => api.get<AssessmentReport>(`${b}/parts/${partId}/assessment/report`),
    /** 测评报告 · 逐题用时(展开时才取;测评不算效率分,只供回看) */
    assessmentTiming: (partId: string) =>
      api.get<LevelTiming>(`${b}/parts/${partId}/assessment/report/timing`),
    packs: () => api.get<DrillPack[]>(`${b}/drill/packs`),
    drillStart: (packId: string, body: { mode: string; knowledgePointIds?: string[]; count?: number }) =>
      api.post<{ session: DrillSession; questions: StudentQuestion[] }>(
        `${b}/drill/packs/${packId}/start`,
        body,
      ),
    drillAnswer: (sessionId: string, body: { questionId: string; answer: unknown; durationSec: number }) =>
      api.post<DrillAnswer>(`${b}/drill/sessions/${sessionId}/answer`, body),
    drillFinish: (sessionId: string) => api.post<DrillSession>(`${b}/drill/sessions/${sessionId}/finish`),
    wrongBook: () => api.get<WrongBook>(`${b}/wrong-book`),
    parentSummary: (studentId: string) => api.get<ParentSummary>(`/v1/parent/children/${studentId}/summary`),
  };
}
export type LearningApi = ReturnType<typeof learningApi>;
